import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';

import { useTownAssets } from '../../stores';
import { logError } from '../../utils';
import CircularProgress from '../CircularProgress';

function SecuredImg({
  piid,
  urlPath,
  className,
  style,
  alt,
  position = 'relative',
  version,
}) {
  const { assets } = useTownAssets();
  const {
    getSecuredImg,
    flushSecuredImg,
  } = assets;

  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Track the previous version to detect changes (not initial load)
  const prevVersionRef = useRef(undefined);

  // Handle version changes - flush cache when version changes (but not on initial load)
  useEffect(
    () => {
    // Only flush if version is defined and has changed from a previous defined value
      if ((version !== undefined)
          && (version !== null)
          && (prevVersionRef.current !== undefined) && (prevVersionRef.current !== null)
          && (version !== prevVersionRef.current)) {
        flushSecuredImg(urlPath, piid);
      }

      // Update the ref after comparison
      prevVersionRef.current = version;
    },
    [version, urlPath, piid, flushSecuredImg],
  );

  useEffect(
    () => {
      const fetchImage = async () => {
        setLoading(true);
        setError(false);

        const img = await getSecuredImg(urlPath, piid);

        if (img) {
          setImageSrc(img);
        } else {
          logError(
            `Failed getSecuredImg urlPath=${urlPath}, piid=${piid}`,
            'SecuredImg.getSecuredImg.failed',
            { urlPath, piid, version },
          );
          setError(true);
        }
        setLoading(false);
      };

      // Use void to make it clear we do not want to wait this async function:
      void fetchImage();
    },
    [piid, getSecuredImg, urlPath, version],
  );

  if (loading) {
    return (
      <div
        className={classNames(
          'flex items-center justify-center',
          position,
          className,
        )}
        style={style}
      >
        <CircularProgress
          customSz
          className="h-full w-full max-w-12 max-h-12"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={classNames(
          position,
          'flex items-center justify-center',
          className,
        )}
        style={style}
      >
        {alt}
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={classNames(
        position,
        className,
      )}
      style={style}
    />
  );
}

SecuredImg.propTypes = {
  piid: PropTypes.string,
  urlPath: PropTypes.string.isRequired,
  className: PropTypes.string,
  style: PropTypes.shape({}),
  alt: PropTypes.string,
  position: PropTypes.string,
  version: PropTypes.number,
};

export default SecuredImg;
